import MoreVertIcon from '@mui/icons-material/MoreVert'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import React from 'react'

import EventBlocker from 'components/common/EventBlocker'
import HHDisplayMoney from 'components/common/HHDisplayMoney'
import Link from 'components/common/Link'
import ServiceDetails from 'components/customer-details/content/configured-services/actions/service-details/ServiceDetails'
import GracePeriodBadgeButton from 'components/pricing/RentalFees/common/badge/GracePeriodBadgeButton'
import RentalFeeBadgeButton from 'components/pricing/RentalFees/common/badge/RentalFeeBadgeButton'
import ServiceActionTypeHelper from 'components/services/ServiceActionTypeHelper'
import TagGroupChips from 'components/tags/TagGroupChips'
import WorkOrderInvoiceStatusChip from 'components/work-order/WorkOrderInvoiceStatusChip'
import WorkOrderStatusChip from 'components/work-order/WorkOrderStatusChip'
import MeasureChip from 'containers/new-route-manager/RouteCard/MeasureChip'
import { getWorkOrderDetailsPageUrl } from 'router/routes'
import { formatDateToFEFormat } from 'utils/date'
import { getMeasureUnit } from 'utils/get-measure-unit'

import DisposalTicketBadge from '../actions/disposal-ticket/DisposalTicketBadge'
import DisposalTicketRow from '../actions/disposal-ticket/DisposalTicketRow'
import ExceptionBadge from '../actions/exception/ExceptionBadge'
import GeneralFeesBadge from '../actions/general-fee/GeneralFeesBadge'
import PhotosBadge from '../actions/PhotosBadge'
import PurchaseOrderBadge from '../actions/PurchaseOrderBadge'
import WONotesBadge from '../actions/WONotesBadge'
import PurchaseOrderChip from '../common/PurchaseOrderChip'
import RouteChip from '../common/RouteChip'
import UnassignedChip from '../common/UnassignedChip'

import type { TagResponse } from 'types/entities/shared'

type Props = {
  workOrderId: string
  workOrderNumber?: string
  configuredServiceId?: string
  locationId?: string
  pricedServiceType?: string
  serviceName?: string
  serviceDate?: string
  workOrderStatus?: string
  intentStatus?: string
  invoiceStatus?: string
  notesCount?: number
  purchaseOrderNumber?: string
  feeTotalCents?: number
  disposalTicketTotalCents?: number
  exceptions?: React.ComponentProps<typeof ExceptionBadge>['exceptions']
  routeName?: string
  routeId?: string
  routeColor?: string
  servicedAt?: string
  servicedByFirstName?: string
  servicedByLastName?: string
  serviceActionName?: string
  eventActionType?: string
  measureUnit?: string
  measureVolume?: number
  locationName?: string
  sequence?: number
  rentalFeeCount?: number
  rentalFeeResetCount?: number
  address?: string
  recurrenceLabel?: string
  tags?: TagResponse
  totalCents?: number
  activeDisposalTickets?: unknown[]
  hasDisposalTicket?: boolean
  hasPurchaseOrder?: boolean
  isLocked?: boolean
  showRentalFee?: boolean
  showPOChip?: boolean
  showDisposalTicket?: boolean
  disposalSites?: React.ComponentProps<typeof DisposalTicketRow>['disposalSites']
  leftBlockProps?: Record<string, unknown>
  rightBlockProps?: Record<string, unknown>
  onSuccess?: () => void
  onOpenActionMenu?: React.MouseEventHandler<HTMLButtonElement>
  onOpenDisposalTicketDialog?: () => void
}

const WorkOrderContent: React.FC<Props> = ({
  workOrderId,
  workOrderNumber,
  configuredServiceId,
  locationId,
  pricedServiceType,
  serviceName,
  serviceDate,
  workOrderStatus,
  intentStatus,
  invoiceStatus,
  notesCount,
  purchaseOrderNumber,
  feeTotalCents,
  disposalTicketTotalCents,
  exceptions,
  routeName,
  routeId,
  routeColor,
  servicedAt,
  servicedByFirstName,
  servicedByLastName,
  serviceActionName,
  eventActionType,
  measureUnit,
  measureVolume,
  locationName,
  sequence,
  rentalFeeCount,
  rentalFeeResetCount,
  address,
  recurrenceLabel,
  tags,
  totalCents = 0,
  activeDisposalTickets,
  hasDisposalTicket,
  hasPurchaseOrder,
  isLocked,
  showRentalFee,
  showPOChip,
  showDisposalTicket,
  disposalSites,
  leftBlockProps,
  rightBlockProps,
  onSuccess,
  onOpenActionMenu,
  onOpenDisposalTicketDialog,
}) => {
  const measureLabel = `${measureVolume} ${getMeasureUnit(measureUnit)}`
  const hasServiceTags = Boolean(tags?.forService.length)
  const hasLocationTags = Boolean(tags?.forLocation.length)

  return (
    <Grid container alignItems="center" gap={2} width="100%">
      <Grid container gap={1} size="grow" {...leftBlockProps}>
        <Grid container alignItems="center" gap={1} justifyContent="space-between" size={12}>
          <Box alignItems="center" display="flex" flexWrap="wrap" gap={1}>
            <EventBlocker>
              <ServiceDetails pricedServiceType={pricedServiceType} serviceId={configuredServiceId} />
            </EventBlocker>
            <EventBlocker>
              <Link color="info.light" fontWeight={500} to={getWorkOrderDetailsPageUrl(workOrderId)} variant="h5" whiteSpace="nowrap">
                {workOrderNumber}
              </Link>
            </EventBlocker>
            <Divider flexItem orientation="vertical" sx={{ borderColor: 'text.secondary' }} />
            <Typography color="textSecondary" fontWeight={500} variant="h6">
              {formatDateToFEFormat(serviceDate)}
            </Typography>
            {totalCents > 0 && (
              <Typography color="textPrimary" fontWeight={700} variant="h5">
                <HHDisplayMoney value={totalCents} />
              </Typography>
            )}
          </Box>
          {showPOChip && hasPurchaseOrder && (
            <EventBlocker component="div" flex={1} minWidth={75}>
              <PurchaseOrderChip
                configuredServiceId={configuredServiceId}
                purchaseOrder={purchaseOrderNumber}
                serviceDate={serviceDate}
                workOrderId={workOrderId}
                onSuccess={onSuccess}
              />
            </EventBlocker>
          )}
          <Box alignItems="center" display="flex" flexWrap="wrap" gap={1}>
            {hasServiceTags && (
              <EventBlocker>
                <TagGroupChips group="forService" tags={tags?.forService} />
              </EventBlocker>
            )}
            <Typography color="textPrimary" variant="subtitle1">
              {serviceName}
            </Typography>
            <Divider flexItem orientation="vertical" sx={{ borderColor: 'text.primary' }} />
            <Typography color="textPrimary" variant="subtitle1">
              {recurrenceLabel}
            </Typography>
          </Box>
        </Grid>
        <Divider sx={{ width: '100%' }} />
        <Grid container alignItems="center" gap={1} justifyContent="space-between" size={12}>
          <Box alignItems="center" display="flex" flexWrap="wrap" gap={1}>
            <Typography color="textPrimary" fontWeight={500} variant="h6">
              {locationName}
            </Typography>
            {hasLocationTags && (
              <EventBlocker>
                <TagGroupChips group="forLocation" tags={tags?.forLocation} />
              </EventBlocker>
            )}
          </Box>
          <Typography variant="subtitle2">{address}</Typography>
        </Grid>
        <Grid container alignItems="center" gap={1} justifyContent="space-between" size={12}>
          <Box alignItems="center" display="flex" flexWrap="wrap" gap={1}>
            <WorkOrderStatusChip
              intentStatus={intentStatus}
              servicedAt={servicedAt}
              servicedBy={`${servicedByFirstName} ${servicedByLastName}`.trim()}
              status={workOrderStatus}
              workOrderId={workOrderId}
            />
            {routeId && (
              <EventBlocker>
                <RouteChip routeColor={routeColor} routeName={routeName} sequence={sequence} serviceDate={serviceDate} />
              </EventBlocker>
            )}
            {!routeId && <UnassignedChip />}
          </Box>
          <Box alignItems="center" display="flex" flexWrap="wrap" gap={1}>
            <ServiceActionTypeHelper
              eventActionType={eventActionType}
              serviceActionType={serviceActionName}
              serviceType={pricedServiceType}
            />
            {measureUnit && <MeasureChip label={measureLabel} measureUnit={measureUnit} />}
            <WorkOrderInvoiceStatusChip status={invoiceStatus} />
          </Box>
        </Grid>
        {showDisposalTicket && hasDisposalTicket && (
          <>
            <Divider sx={{ width: '100%' }} />
            <Grid container alignItems="center" gap={1} justifyContent="space-between" width="100%" wrap="wrap">
              <EventBlocker width="100%">
                <DisposalTicketRow
                  disposalSites={disposalSites}
                  disposalTickets={activeDisposalTickets}
                  disposalTicketTotalCents={disposalTicketTotalCents}
                  onOpenDisposalTicketDialog={onOpenDisposalTicketDialog}
                />
              </EventBlocker>
            </Grid>
          </>
        )}
      </Grid>
      <Grid {...rightBlockProps}>
        <ListItem disablePadding>
          {showRentalFee && (
            <ListItemText
              primary={
                <EventBlocker>
                  <RentalFeeBadgeButton disableRipple configuredServiceId={configuredServiceId} rentalFeeCount={rentalFeeCount} />
                </EventBlocker>
              }
              secondary={
                <EventBlocker>
                  <GracePeriodBadgeButton showTooltip configuredServiceId={configuredServiceId} rentalFeeResetCount={rentalFeeResetCount} />
                </EventBlocker>
              }
              sx={{ mr: 2 }}
            />
          )}
          <ListItemText
            primary={
              <EventBlocker>
                <ExceptionBadge exceptions={exceptions} />
              </EventBlocker>
            }
            secondary={
              <EventBlocker>
                <PhotosBadge exceptions={exceptions} />
              </EventBlocker>
            }
            sx={{ mr: 2 }}
          />
          <ListItemText
            primary={
              <EventBlocker>
                <GeneralFeesBadge
                  configuredServiceId={configuredServiceId}
                  isLocked={isLocked}
                  serviceDate={serviceDate}
                  totalCents={feeTotalCents}
                  workOrderId={workOrderId}
                  onSuccess={onSuccess}
                />
              </EventBlocker>
            }
            secondary={
              <EventBlocker>
                <DisposalTicketBadge
                  configuredServiceId={configuredServiceId}
                  hasDisposalTicket={hasDisposalTicket}
                  totalCents={disposalTicketTotalCents}
                  workOrderId={workOrderId}
                  onOpenDisposalTicketDialog={onOpenDisposalTicketDialog}
                />
              </EventBlocker>
            }
            sx={{ mr: 2 }}
          />
          <ListItemText
            primary={
              <EventBlocker>
                <PurchaseOrderBadge
                  configuredServiceId={configuredServiceId}
                  purchaseOrder={purchaseOrderNumber}
                  serviceDate={serviceDate}
                  workOrderId={workOrderId}
                  onSuccess={onSuccess}
                />
              </EventBlocker>
            }
            secondary={
              <EventBlocker>
                <WONotesBadge
                  workOrderFirst
                  configuredServiceId={configuredServiceId}
                  locationId={locationId}
                  notesCount={notesCount}
                  workOrderId={workOrderId}
                />
              </EventBlocker>
            }
            sx={{ mr: 2 }}
          />
          <ListItemIcon sx={{ minWidth: 'auto' }}>
            <EventBlocker>
              <IconButton size="small" onClick={onOpenActionMenu}>
                <MoreVertIcon />
              </IconButton>
            </EventBlocker>
          </ListItemIcon>
        </ListItem>
      </Grid>
    </Grid>
  )
}

export default WorkOrderContent
